import { ModalDialogContainer } from '@common/modal'
import { useModal } from '@common/GlobalModal'
import React, { useEffect, useState } from 'react'
import { SelectSiteBoxDocumentModal } from '@modules/siteBox/modals/SelectSiteBoxDocumentModal'
import { SelectSiteBoxDocumentModalLegacy } from '@modules/siteBox/modals/SelectSiteBoxDocumentModalLegacy'
import { localizedStrings } from '@core/strings'
import { useAddRegisterDiagram } from '@core/react-query/features/registers/registers/register/hooks'
import { useToast } from '@core/toast'
import { useGetSiteBoxDocument } from '@core/react-query/features/siteBoxDocuments'
import { SiteBoxDocumentPdfVersion } from '@core/entities/SiteBoxDocumentPdfVersion'
import BackdropLoader from '@common/fundamentals/BackdropLoader'
import useFeatureFlags from '@core/providers/useFeatureFlags'
import { SiteBoxDocument } from '@core/entities/SiteBoxDocument'

type RegisterAddDiagramModalProps = {
  organisationId: number
  projectId: number
  registerId: number
  onSuccess?: () => void
}

export const RegisterAddDiagramModal: React.FC<
  RegisterAddDiagramModalProps
> = ({ organisationId, projectId, registerId, onSuccess }) => {
  const { showModal, hideModal } = useModal()
  const toast = useToast()
  const { flags } = useFeatureFlags(['web_site_box_rams_appraisals'])

  const addRegisterDiagram = useAddRegisterDiagram()
  const [siteboxDocumentId, setSiteboxDocumentId] = useState<number>()

  useGetSiteBoxDocument(
    {
      documentId: siteboxDocumentId,
    },
    {
      enabled: !!siteboxDocumentId,
      refetchInterval: (result) => {
        const version = result?.currentVersion

        if (
          version instanceof SiteBoxDocumentPdfVersion &&
          version.imageProcessingStatus === 'DONE'
        ) {
          toast.success(localizedStrings.documentAdded)
          hideModal()
          onSuccess?.()
          return null
        }

        if (
          version instanceof SiteBoxDocumentPdfVersion &&
          version.imageProcessingStatus === 'FAILED'
        ) {
          toast.error(localizedStrings.uploadFailed)
          hideModal()
          return null
        }

        return 1000
      },
    },
  )

  useEffect(() => {
    const handleSelection = (siteboxDocuments: SiteBoxDocument) => {
      const picked = Array.isArray(siteboxDocuments)
        ? siteboxDocuments[0]
        : siteboxDocuments
      if (!picked) return
      addRegisterDiagram.mutate(
        {
          registerId: registerId,
          siteBoxDocumentId: picked.id,
        },
        {
          onSuccess: () => {
            setSiteboxDocumentId(picked.id)
          },
          onError: (e) => toast.error(e.message),
        },
      )
    }

    if (flags.web_site_box_rams_appraisals) {
      showModal(
        <SelectSiteBoxDocumentModal
          onClose={hideModal}
          organisationId={organisationId}
          projectId={projectId}
          onChange={handleSelection}
        />,
      )
    } else {
      showModal(
        <SelectSiteBoxDocumentModalLegacy
          onClose={hideModal}
          organisationId={organisationId}
          projectId={projectId}
          handleSiteBoxDocumentSelection={handleSelection}
        />,
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <ModalDialogContainer>
      <BackdropLoader open={!!siteboxDocumentId} />
    </ModalDialogContainer>
  )
}
